/**
 * Codici regalo e inviti dei creator (ADR 0043).
 *
 * Le rotte dei codici sono di `identity` (prefisso del gateway `/api/v1/gift-codes`), come
 * quelle dei piani regalati (ADR 0061, sotto `/api/v1/users/{sub}/plan/grants`); le
 * commissioni del creator di `billing` (`/api/v1/billing/commissions`). Provvisorio
 * dichiarato, come gli altri client: percorsi scritti a mano invece che generati dagli
 * schemi OpenAPI. `giftCodes.test.ts` presidia metodo, percorso e corpo di ogni chiamata.
 */

import type { ApiClient } from './client';

/** Prefisso delle rotte dei codici regalo. */
const GIFT = '/api/v1/gift-codes';

/**
 * Percorso dei regali di piano di un utente (`identity`, ADR 0061).
 *
 * @param subjectId Utente.
 * @returns Il percorso.
 */
function userGrants(subjectId: string): string {
  return `/api/v1/users/${encodeURIComponent(subjectId)}/plan/grants`;
}

/** Codici d'errore del riscatto (ADR 0043 §2.2), nell'ordine in cui il backend li controlla. */
export const REDEEM_ERRORS = [
  'gift_code_not_found',
  'gift_code_disabled',
  'gift_code_expired',
  'gift_code_exhausted',
  'gift_code_already_redeemed',
  'gift_code_not_for_you',
  'gift_code_own_code',
] as const;

/** Codice d'errore del riscatto. */
export type RedeemError = (typeof REDEEM_ERRORS)[number];

/** Tipo di codice: per una persona o da diffondere. */
export type GiftCodeKind = 'single' | 'group';

/** Un codice regalo, come lo vede l'amministratore. */
export interface GiftCode {
  /** Identificativo. */
  id: string;
  /** Codice, `XXXX-XXXX-XXXX`. */
  code: string;
  /** Tipo. */
  kind: GiftCodeKind;
  /** Token regalati a ogni riscatto. */
  tokens: number;
  /** Riscatti ammessi; `null` senza limite (solo `group`). */
  max_redemptions: number | null;
  /** Riscatti fatti. */
  redemptions: number;
  /** Persone invitate (primo riscatto di un codice con creator). */
  referred: number;
  /** Utente a cui è riservato, come etichetta leggibile; `null` se per chiunque. */
  restricted_to: string | null;
  /** Creator, come etichetta leggibile; `null` se nessuno. */
  creator: string | null;
  /** Percentuale del creator; `null` senza creator. */
  commission_percent: number | null;
  /** Scadenza ISO 8601; `null` se non scade. */
  expires_at: string | null;
  /** True se disattivato. */
  disabled: boolean;
  /** Nota dell'amministratore. */
  note: string;
  /** Creazione, ISO 8601. */
  created_at: string;
}

/** Corpo della creazione di un codice. */
export interface NewGiftCode {
  /** Tipo. */
  kind: GiftCodeKind;
  /** Token per riscatto. */
  tokens: number;
  /** Riscatti ammessi (solo `group`); assente o `null` senza limite. */
  max_redemptions?: number | null;
  /** `sub`, nome utente o email della persona a cui è riservato. */
  restricted_to?: string | null;
  /** `sub`, nome utente o email del creator (solo `group`). */
  creator?: string | null;
  /** Percentuale del creator, 0-50. */
  commission_percent?: number | null;
  /** Scadenza ISO 8601. */
  expires_at?: string | null;
  /** Nota. */
  note?: string;
}

/** Esito di un riscatto. */
export interface Redemption {
  /** Codice riscattato. */
  code: string;
  /** Token ricevuti. */
  tokens: number;
  /** Istante del riscatto, ISO 8601. */
  redeemed_at: string;
}

/** I riscatti del chiamante. */
export interface MyGiftCodes {
  /** Token regalati in totale. */
  bonus_tokens: number;
  /** Token regalati non ancora usati; `null` se il backend non lo sa dire. */
  bonus_remaining?: number | null;
  /** Riscatti, dal più recente. */
  redemptions: Redemption[];
}

/** Un codice di cui il chiamante è creator. */
export interface ReferralCode {
  /** Identificativo. */
  id: string;
  /** Codice. */
  code: string;
  /** Riscatti fatti. */
  redemptions: number;
  /** Riscatti ammessi; `null` senza limite. */
  max_redemptions: number | null;
  /** Persone invitate. */
  referred: number;
  /** Percentuale del creator. */
  commission_percent: number;
  /** True se disattivato. */
  disabled: boolean;
  /** Scadenza ISO 8601; `null` se non scade. */
  expires_at: string | null;
}

/** I codici di cui il chiamante è creator. */
export interface MyReferrals {
  /** Codici. */
  codes: ReferralCode[];
}

/** Una commissione maturata, senza dati dell'invitato. */
export interface Commission {
  /** Etichetta anonima dell'invitato, es. «invitato 3». */
  referred_label: string;
  /** Importo pagato dall'invitato, in centesimi. */
  amount_paid: number;
  /** Valuta, `eur`. */
  currency: string;
  /** Percentuale applicata. */
  commission_percent: number;
  /** Commissione, in centesimi. */
  commission_amount: number;
  /** Istante del pagamento, ISO 8601. */
  created_at: string;
}

/** Le commissioni del creator (`billing`, ADR 0043 §2.4). */
export interface Commissions {
  /** Totale per valuta, in centesimi. */
  totals: Record<string, number>;
  /** Invitati diversi che hanno pagato. */
  referred_count: number;
  /** Righe, dalla più recente. */
  items: Commission[];
}

/** Piano che si può regalare (ADR 0061). */
export type GiftablePlan = 'base' | 'premium';

/** Un piano regalato da un amministratore per alcuni mesi (ADR 0061). */
export interface PlanGrant {
  /** Identificativo. */
  id: string;
  /** Utente che riceve il regalo. */
  subject_id: string;
  /** Utente come etichetta leggibile (email o nome utente). */
  user: string;
  /** Piano regalato. */
  plan: string;
  /** Mesi regalati. */
  months: number;
  /** Inizio, ISO 8601; dopo adesso se il regalo si accoda a un altro. */
  starts_at: string;
  /** Fine, ISO 8601. */
  ends_at: string;
  /** Amministratore che ha fatto il regalo, come etichetta leggibile. */
  granted_by: string;
  /** Nota, vuota se assente. */
  note: string;
  /** Revoca, ISO 8601; `null` se il regalo vale. */
  revoked_at: string | null;
  /** Istante del regalo, ISO 8601. */
  created_at: string;
  /** True se il regalo vale adesso. */
  active: boolean;
}

/** Corpo del regalo di un piano. */
export interface NewPlanGrant {
  /** Piano regalato. */
  plan: GiftablePlan;
  /** Mesi, da 1 a 24. */
  months: number;
  /** Nota facoltativa. */
  note?: string;
}

/**
 * Normalizza un codice scritto dall'utente: niente spazi e trattini, maiuscole.
 *
 * @param raw Testo inserito.
 * @returns Il codice normalizzato (vuoto se non resta nulla).
 */
export function normaliseGiftCode(raw: string): string {
  return raw.replace(/[\s-]+/g, '').toUpperCase();
}

/**
 * True se il valore è un codice d'errore del riscatto.
 *
 * @param code Codice dell'errore.
 * @returns True per i codici di ADR 0043 §2.2.
 */
export function isRedeemError(code: string | null): code is RedeemError {
  return code !== null && (REDEEM_ERRORS as readonly string[]).includes(code);
}

/** Client dei codici regalo. */
export class GiftCodesApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Elenco dei codici (amministratore).
   *
   * @returns I codici, dal più recente.
   */
  list(): Promise<GiftCode[]> {
    return this.client.request<GiftCode[]>('GET', GIFT);
  }

  /**
   * Crea un codice (amministratore).
   *
   * @param body Dati del codice.
   * @returns Il codice creato.
   */
  create(body: NewGiftCode): Promise<GiftCode> {
    return this.client.request<GiftCode>('POST', GIFT, body);
  }

  /**
   * Attiva o disattiva un codice (amministratore).
   *
   * @param id Codice.
   * @param disabled True per disattivarlo.
   * @returns Il codice aggiornato.
   */
  setDisabled(id: string, disabled: boolean): Promise<GiftCode> {
    return this.client.request<GiftCode>('PATCH', `${GIFT}/${encodeURIComponent(id)}`, { disabled });
  }

  /**
   * Riscatta un codice.
   *
   * @param code Codice come l'ha scritto l'utente; si normalizza qui.
   * @returns Il riscatto.
   */
  redeem(code: string): Promise<Redemption> {
    return this.client.request<Redemption>('POST', `${GIFT}/redeem`, { code: normaliseGiftCode(code) });
  }

  /**
   * Riscatti del chiamante.
   *
   * @returns Riscatti e totale.
   */
  mine(): Promise<MyGiftCodes> {
    return this.client.request<MyGiftCodes>('GET', `${GIFT}/mine`);
  }

  /**
   * Codici di cui il chiamante è creator.
   *
   * @returns I codici.
   */
  referrals(): Promise<MyReferrals> {
    return this.client.request<MyReferrals>('GET', `${GIFT}/referrals`);
  }

  /**
   * Regala un piano a un utente (amministratore, `identity`, ADR 0061).
   *
   * @param subjectId `sub`, nome utente o email dell'utente.
   * @param body Piano, mesi e nota.
   * @returns Il regalo creato.
   */
  grantPlan(subjectId: string, body: NewPlanGrant): Promise<PlanGrant> {
    return this.client.request<PlanGrant>('POST', userGrants(subjectId), body);
  }

  /**
   * Regali in corso o accodati di tutti gli utenti (amministratore).
   *
   * @returns I regali, dalla fine più vicina.
   */
  planGrants(): Promise<PlanGrant[]> {
    return this.client.request<PlanGrant[]>('GET', `${GIFT}/plan-grants`);
  }

  /**
   * Tutti i regali di un utente, anche scaduti e revocati (amministratore).
   *
   * @param subjectId Utente.
   * @returns I regali, dal più recente.
   */
  userPlanGrants(subjectId: string): Promise<PlanGrant[]> {
    return this.client.request<PlanGrant[]>('GET', userGrants(subjectId));
  }

  /**
   * Revoca un regalo (amministratore).
   *
   * @param subjectId Utente del regalo.
   * @param grantId Regalo.
   * @returns Il regalo revocato.
   */
  revokePlanGrant(subjectId: string, grantId: string): Promise<PlanGrant> {
    return this.client.request<PlanGrant>(
      'DELETE',
      `${userGrants(subjectId)}/${encodeURIComponent(grantId)}`,
    );
  }

  /**
   * Commissioni maturate dal creator (servizio `billing`).
   *
   * @returns Totale e righe.
   */
  commissions(): Promise<Commissions> {
    return this.client.request<Commissions>('GET', '/api/v1/billing/commissions');
  }
}
